import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useSearchParams, useLocation, useNavigationType } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr, parsePayload } from '@/features/shared/lib/utils';
import DepartmentsCard from '@/features/department/components/DepartmentsCard';
import NumberOfResourcesByCollege from '@/features/department/chart/NumberOfResourcesByCollege';
import CollegeRateByDBChart from '@/features/department/chart/CollegeRateByDBChart';

export default function Departments() {
  // data
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [collegeStatsData, setCollegeStatsData] = useState(null);
  const [collegeStatsLoading, setCollegeStatsLoading] = useState(false);
  const [collegeStatsErr, setCollegeStatsErr] = useState(null);

  // URL filter for college (by code/value)
  const [sp, setSp] = useSearchParams();
  const collegeParam = sp.get('college') || '';
  // graph toggle state
  const [graphExpanded, setGraphExpanded] = useState(true);

  // 년도 계산: 3월 전까지는 전년도, 3월 이후는 올해
  const displayYear = useMemo(() => {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    const currentYear = now.getFullYear();
    // 3월 전(1월, 2월)이면 전년도, 3월 이후면 올해
    return month < 3 ? currentYear - 1 : currentYear;
  }, []);

  // Back-only hydration helpers (cache + detection)
  const location = useLocation();
  const navType = useNavigationType();
  const hydratedRef = useRef(false);
  const cacheKey = useMemo(() => {
    try {
      const { pathname, search } = window.location;
      return `DEPARTMENTS_CACHE::${pathname}${search}`;
    } catch (e) {
      return 'DEPARTMENTS_CACHE';
    }
  }, [location]);

  const loadCache = () => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const saveCache = (payload) => {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (e) { }
  };

  const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
  const isValidCache = (c) => {
    if (!c) return false;
    if (!Array.isArray(c.colleges) || c.colleges.length === 0) return false;
    if (typeof c.ts !== 'number') return true; // backward compatibility
    return (Date.now() - c.ts) <= CACHE_TTL_MS;
  };

  const isBackForwardNav = () => {
    try {
      const navs = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (navs && navs.length) {
        const type = navs[0]?.type;
        if (type === 'back_forward') return true;
        if (type === 'reload') return false;
        return false;
      }
    } catch (e) { }
    try {
      const idx = window.history && window.history.state && window.history.state.idx;
      const sameOriginReferrer = document.referrer && document.referrer.startsWith(window.location.origin);
      if (typeof idx === 'number' && idx > 0 && sameOriginReferrer) return true;
    } catch (e) { }
    return false;
  };

  // Pre-paint hydrate when coming back with a valid cache
  useLayoutEffect(() => {
    if (navType !== 'POP' || hydratedRef.current) return;
    const cached = loadCache();
    if (isValidCache(cached)) {
      hydratedRef.current = true;
      setColleges(cached.colleges || []);
      try {
        window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' });
      } catch (e) { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType]);

  useEffect(() => {
    // On true back/forward with valid cache, skip initial fetch
    if (navType === 'POP' && isBackForwardNav()) {
      const cached = loadCache();
      if (isValidCache(cached)) {
        hydratedRef.current = true;
        if (!Array.isArray(colleges) || colleges.length === 0) {
          setColleges(cached.colleges || []);
        }
        setTimeout(() => {
          try { window.scrollTo({ top: cached.scrollY || 0, left: 0, behavior: 'auto' }); } catch (e) { }
        }, 0);
        return; // skip initial fetch
      }
    }

    let mounted = true;
    async function fetchDepartments() {
      setLoading(true);
      setErr(null);
      try {
        // API base is /scholar/wp-json (see shared/api.js). Endpoint path only.
        const data = await get('/rm/v1/departments', { params: { disp_yn: 'Y', del_yn: 'N' } });
        const { status, records } = parsePayload(data);
        if (!status) throw new Error('API status=false');
        const next = arr(records);
        if (mounted) setColleges(next);
        // Save cache only with real data
        saveCache({
          colleges: next,
          scrollY: (typeof window !== 'undefined' ? window.scrollY || 0 : 0),
          ts: Date.now(),
        });
      } catch (e) {
        if (mounted) setErr(e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchDepartments();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navType]);

  useEffect(() => {
    const site =
      typeof rmVars !== 'undefined' && rmVars.siteName
        ? String(rmVars.siteName).trim()
        : '';
    if (!collegeParam) {
      if (site) document.title = `Departments - ${site}`;
      return;
    }
    const match = arr(colleges).find(
      (c) => String(c?.value ?? c?.category_code ?? '') === String(collegeParam)
    );
    const label = match?.category_name ? String(match.category_name).trim() : '';
    if (!label) return;
    document.title = site ? `${label} - Department - ${site}` : label;
  }, [collegeParam, colleges]);

  useEffect(() => {
    let alive = true;
    async function fetchCollegeStats() {
      setCollegeStatsLoading(true);
      setCollegeStatsErr(null);
      try {
        const response = await get('/rm/v1/stats/college_stats');
        if (alive && response?.status && response?.result?.records) {
          setCollegeStatsData(response.result);
        } else if (alive) {
          setCollegeStatsData(null);
        }
      } catch (e) {
        if (alive) setCollegeStatsErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setCollegeStatsLoading(false);
      }
    }
    fetchCollegeStats();
    return () => { alive = false; };
  }, []);

  // Persist scroll position efficiently
  useEffect(() => {
    const onScroll = () => {
      const cached = loadCache();
      const now = Date.now();
      if (Array.isArray(colleges) && colleges.length > 0) {
        saveCache({ colleges, scrollY: window.scrollY || 0, ts: now });
      } else if (isValidCache(cached)) {
        cached.scrollY = window.scrollY || 0;
        cached.ts = now;
        saveCache(cached);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [colleges]);

  const filteredColleges = useMemo(() => {
    if (!collegeParam) return colleges;
    return arr(colleges).filter(c => String(c?.value ?? c?.category_code ?? '') === String(collegeParam));
  }, [colleges, collegeParam]);

  const onClickCollegeChip = (c) => {
    const code = c?.value ?? c?.category_code;
    if (!code) return;
    const next = new URLSearchParams(sp);
    // toggle behavior: clicking same chip clears filter
    if (String(collegeParam) === String(code)) {
      next.delete('college');
    } else {
      next.set('college', String(code));
    }
    setSp(next, { replace: false });
  };

  return (<div className="departmentslistPage listPage mt-[127px] lg:mt-[120px] 2xl:px-[100px]">
    <div className="w-full px-6 pt-[30px] 2xl:px-0">
      <div className="bread mb-[27px] text-[#8D9AAF] text-[13px] flex items-center gap-1 lg:mb-[20px] lg:items-start">
        <a href="/" className="flex items-center gap-[2px] shrink-0">
          <img src={rmVars.themeUrl + 'images/home-ico.png'} className="w-[13px]" alt="home" />
          Home
        </a>
        <img src={rmVars.themeUrl + 'images/chevron_right_8D9AAF.png'} className="w-[12px] shrink-0 lg:translate-y-[4px]" alt="right" />
        <span className="current font-bold">Departments</span>
      </div>

      <div className="tit-area w-full flex justify-between gap-3">
        <h2 className="text-[26px] leading-[130%] font-black lg:text-[32px]">
          Departments
        </h2>
      </div>

      <div className="mt-[18px] bg-white rounded-xl border border-[#E4E8F1] lg:mt-[37px] lg:border-none">

        <div
          className="journal-graph-toggle relative pt-[24px] pb-6 px-[20px] cursor-pointer lg:px-[25px] lg:pb-5 lg:pt-[20px]"
          onClick={() => setGraphExpanded(!graphExpanded)}
        >
          <div className="flex items-center gap-3 pr-[30px] text-[15px] font-bold leading-[130%] lg:text-[18px] lg:gap-[6px]">
            <div className="flex shrink-0 justify-center items-center w-[38px] h-[38px] rounded-lg bg-[#E4E8F1] lg:w-auto lg:h-auto lg:bg-transparent">
              <img src={rmVars.themeUrl + 'images/bar_chart_ico.png'} className="w-[22px]" alt="chart icon" />
            </div>
            Exploring Our College Research Trends
          </div>
          <img
            src={rmVars.themeUrl + 'images/expand_more_333.png'}
            className={`absolute right-[20px] top-[50%] w-[20px] translate-y-[-50%] transition-transform duration-300 lg:w-[24px] ${graphExpanded ? 'rotate-180' : ''}`}
            alt="toggle"
          />
        </div>

        {graphExpanded && (
          <div className="journal-graph-cont border-t border-t-[#E4E8F1] pt-5 px-[20px] lg:pt-[32px] lg:flex gap-[20px] lg:pb-[36px] relative">
            {collegeStatsLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10 rounded-b-xl">
                <div className="w-12 h-12 border-4 border-[#E4E8F1] border-t-[#6EA2FF] rounded-full animate-spin" />
              </div>
            )}
            {collegeStatsErr && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[#F3652D] text-sm z-10">{collegeStatsErr}</div>
            )}

            <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-20px)/2)] lg:bg-[#F3F5F9] lg:p-8 lg:mb-0">
              <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center lg:text-[16px]">
                Number of Publications by College in {displayYear}
              </h3>
              <NumberOfResourcesByCollege records={collegeStatsData?.records ?? []} />
            </div>

            <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-20px)/2)] lg:bg-[#F3F5F9] lg:p-8 lg:mb-0 overflow-visible">
              <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center">
                Rate by College {displayYear}
              </h3>
              <CollegeRateByDBChart records={collegeStatsData?.records ?? []} />
            </div>

            <div className="flex justify-center pb-5 lg:hidden">
              <button
                className="flex items-center gap-[2px] px-[10px] py-1 text-[13px] font-semibold text-[#8D9AAF] cursor-pointer"
                onClick={() => setGraphExpanded(false)}
              >
                <img src={rmVars.themeUrl + 'images/expand_more_8D9AAF.png'} className="w-[14px] rotate-180" alt="hide" />
                Hide Chart
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="college-find mt-[40px] lg:bg-white lg:p-10 lg:rounded-[12px] lg:mt-5">
        <p className="mb-3 text-[14px] font-bold">단대별로 찾기</p>

        <div className="overflow-x-auto snap-x snap-mandatory lg:overflow-x-visible">
          <ul className="flex flex-wrap gap-2 w-[1600px] text-[14px] text-[#6C7687] leading-[27px] snap-start lg:w-full">
            {/* 전체 보기 */}
            <li
              className={`px-3 h-[27px] rounded-full cursor-pointer ${!collegeParam ? 'bg-[#E4E8F1] font-semibold' : 'bg-[#E4E8F1]'}`}
              onClick={() => {
                const next = new URLSearchParams(sp);
                next.delete('college');
                setSp(next, { replace: false });
              }}
            >전체</li>
            {arr(colleges).map((c) => {
              const code = c?.value ?? c?.category_code;
              const active = String(collegeParam) === String(code);
              return (
                <li
                  key={code || c?.category_id}
                  className={`px-3 h-[27px] rounded-full cursor-pointer ${active ? 'bg-[#E4E8F1] font-semibold' : 'bg-[#E4E8F1]'}`}
                  onClick={() => onClickCollegeChip(c)}
                  title={c?.category_name}
                >
                  {c?.category_name}
                </li>
              );
            })}
          </ul>
        </div>

      </div>

      <div className="flex flex-col gap-4 mt-[25px] lg:gap-5 lg:mt-5">
        {loading && (
          <div className="p-6 bg-white rounded-[12px] border border-[#E4E8F1] text-[#6C7687]">Loading...</div>
        )}
        {err && (
          <div className="p-6 bg-white rounded-[12px] border border-[#E4E8F1] text-red-500">Failed to load departments.</div>
        )}

        {arr(filteredColleges).map((college) => {
          const code = college?.value ?? college?.category_code;
          return (
            <DepartmentsCard key={code || college?.category_id} college={college} />
          );
        })}
      </div>
    </div>
  </div>);
}
